import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Award, Loader2, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentResults() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchResults();
  }, [navigate]);

  const fetchResults = async () => {
    const token = localStorage.getItem('studentToken');
    
    try {
      const response = await fetch(`${API_URL}/api/student-portal/results`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setResults(data.results || []);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      toast.error('Failed to load results');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-xl font-semibold">Exam Results</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {results.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Award className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Published</h3>
              <p className="text-gray-500">Your exam results will appear here once published.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {results.map((result, idx) => (
              <Card key={idx}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{result.exam_name}</CardTitle>
                    <span className="text-sm text-gray-500">{result.exam_date}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Subject</th>
                          <th className="px-4 py-2 text-center text-sm font-medium text-gray-500">Max Marks</th>
                          <th className="px-4 py-2 text-center text-sm font-medium text-gray-500">Obtained</th>
                          <th className="px-4 py-2 text-center text-sm font-medium text-gray-500">Grade</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {result.subjects?.map((subject, sidx) => (
                          <tr key={sidx}>
                            <td className="px-4 py-2 text-sm">{subject.name}</td>
                            <td className="px-4 py-2 text-sm text-center">{subject.max_marks}</td>
                            <td className="px-4 py-2 text-sm text-center font-medium">{subject.obtained_marks}</td>
                            <td className="px-4 py-2 text-sm text-center">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                subject.grade === 'A+' || subject.grade === 'A' ? 'bg-green-100 text-green-700' :
                                subject.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                                subject.grade === 'C' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {subject.grade}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td className="px-4 py-2 text-sm font-medium">Total</td>
                          <td className="px-4 py-2 text-sm text-center font-medium">{result.total_max}</td>
                          <td className="px-4 py-2 text-sm text-center font-bold text-indigo-600">{result.total_obtained}</td>
                          <td className="px-4 py-2 text-sm text-center font-bold">{result.percentage}%</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
